// Printer-friendly production plan: black-and-white US Letter pages with a
// checkbox per flavor, then the ingredients needed. Hand-built PDF like the
// other *Pdf.ts helpers (Helvetica, no dependencies).

import { formatAmount } from './format'

type BagSize = 'Small' | 'Large' | 'Other'

export interface PrintPlanProduct {
  size: BagSize
  open_qty: number
  forecast_qty: number
  in_stock: number
  to_make: number
}

export interface PrintPlanFlavor {
  label_name: string
  has_recipe: boolean
  base_batch_weight_oz: number | null
  products: PrintPlanProduct[]
  oz_to_make: number
  batches: number | null
  batches_by_weight?: number | null
  missing_weight: boolean
  blend_oz?: number
  used_in?: string[]
  made_from?: { label_name: string; pct: number | null }[]
  blend_by_percent?: boolean
  recipe_only?: boolean
  tier?: string | null
  tier_min?: number | null
}

export interface PrintPlanIngredient {
  name: string
  amount: number
  unit: string | null
  flavors: string[]
}

export interface PrintPlan {
  includeForecast: boolean
  forecast: { weekStart: string; weekEnd: string } | null
  forecastStale?: boolean
  flavors: PrintPlanFlavor[]
  ingredients: PrintPlanIngredient[]
}

// ─── Page geometry (US Letter, points) ──────────────────────────────────────
// Shared with the other hand-built PDFs that import from here.
export const PAGE_W = 612
export const PAGE_H = 792
export const MARGIN = 50
export const RIGHT = PAGE_W - MARGIN
const BOTTOM_LIMIT = 50
export const LINE_H = 11

const X_BOX = MARGIN + 2
const X_FLAVOR = MARGIN + 18
const X_WEIGHT = MARGIN + 380
const X_BATCHES = MARGIN + 450
// Bag-size columns sit between the flavor and the weight.
const SIZE_X = [MARGIN + 220, MARGIN + 270, MARGIN + 320]

const X_INGREDIENT = MARGIN + 4
const X_AMOUNT = MARGIN + 200
const X_USED_IN = MARGIN + 290

// ─── helpers ─────────────────────────────────────────────────────────────────

function num(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

function fmt(n: number, digits = 1): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: digits })
}

export function weight(oz: number): string {
  return oz >= 16 ? `${fmt(oz / 16)} lb` : `${fmt(oz)} oz`
}

export function formatDay(value: string): string {
  const d = new Date(`${value}T00:00:00`)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** Escape a text string for a PDF literal and drop chars outside WinAnsi (>255). */
function pdfText(s: string): string {
  let out = ''
  for (const ch of s) {
    const code = ch.codePointAt(0) ?? 0
    if (code > 255) {
      out += ch === '’' ? "'" : ch === '–' || ch === '—' ? '-' : '?'
      continue
    }
    const c = String.fromCharCode(code)
    if (c === '\\') out += '\\\\'
    else if (c === '(') out += '\\('
    else if (c === ')') out += '\\)'
    else out += c
  }
  return out
}

/** Splits text into lines of at most `max` characters, breaking at spaces. */
export function wrap(value: string, max: number): string[] {
  const lines: string[] = []
  let current = ''
  for (const word of value.split(/\s+/).filter(Boolean)) {
    if (current && current.length + 1 + word.length > max) {
      lines.push(current)
      current = word
    } else {
      current = current ? `${current} ${word}` : word
    }
  }
  if (current) lines.push(current)
  return lines.length > 0 ? lines : ['']
}

export function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 3)}...` : value
}

// ─── content-stream operator builders ────────────────────────────────────────

export function text(x: number, y: number, size: number, font: 'F1' | 'F2', value: string, gray = 0): string {
  return `${num(gray)} g BT /${font} ${num(size)} Tf 1 0 0 1 ${num(x)} ${num(y)} Tm (${pdfText(value)}) Tj ET 0 g\n`
}

export function line(x1: number, y1: number, x2: number, y2: number, width = 1, gray = 0): string {
  return `${num(width)} w ${num(gray)} ${num(gray)} ${num(gray)} RG ${num(x1)} ${num(y1)} m ${num(x2)} ${num(y2)} l S\n`
}

export function strokeRect(x: number, y: number, w: number, h: number, width = 1): string {
  return `${num(width)} w 0 0 0 RG ${num(x)} ${num(y)} ${num(w)} ${num(h)} re S\n`
}

// ─── PDF assembly ────────────────────────────────────────────────────────────

export function buildPdf(pageContents: string[]): ArrayBuffer {
  const pageCount = pageContents.length
  const kids: string[] = []
  for (let i = 0; i < pageCount; i++) kids.push(`${5 + 2 * i} 0 R`)

  const objects: { num: number; body: string }[] = []
  objects.push({ num: 1, body: '<< /Type /Catalog /Pages 2 0 R >>' })
  objects.push({ num: 2, body: `<< /Type /Pages /Kids [${kids.join(' ')}] /Count ${pageCount} >>` })
  objects.push({ num: 3, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>' })
  objects.push({ num: 4, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>' })

  for (let i = 0; i < pageCount; i++) {
    const pageObj = 5 + 2 * i
    const contentObj = 6 + 2 * i
    objects.push({
      num: pageObj,
      body: `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObj} 0 R >>`,
    })
    const content = pageContents[i] ?? ''
    objects.push({
      num: contentObj,
      body: `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    })
  }

  objects.sort((a, b) => a.num - b.num)

  let pdf = '%PDF-1.4\n'
  const offsets: number[] = []
  for (const obj of objects) {
    offsets[obj.num] = pdf.length
    pdf += `${obj.num} 0 obj\n${obj.body}\nendobj\n`
  }

  const xrefOffset = pdf.length
  const total = objects.length + 1
  pdf += `xref\n0 ${total}\r\n`
  pdf += '0000000000 65535 f\r\n'
  for (let n = 1; n < total; n++) {
    pdf += `${String(offsets[n] ?? 0).padStart(10, '0')} 00000 n\r\n`
  }
  pdf += `trailer\n<< /Size ${total} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  const buffer = new ArrayBuffer(pdf.length)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff
  return buffer
}

// ─── page builder ────────────────────────────────────────────────────────────

export class Pages {
  pages: string[] = []
  ops: string[] = []
  top = PAGE_H - MARGIN

  push(op: string) {
    this.ops.push(op)
  }

  /** Starts a new page when `height` doesn't fit; returns true if it did. */
  ensure(height: number): boolean {
    if (this.top - height >= BOTTOM_LIMIT) return false
    this.flush()
    return true
  }

  flush() {
    this.pages.push(this.ops.join(''))
    this.ops = []
    this.top = PAGE_H - MARGIN
  }
}

function sizeDetail(p: PrintPlanProduct): string {
  let s = `${p.open_qty}`
  if (p.forecast_qty > 0) s += ` + ${p.forecast_qty}`
  if (p.in_stock > 0) s += ` - ${p.in_stock}`
  return s
}

/** Set when the batch count was raised to one batch per use (each blend, plus on its own). */
export function perUseBatchNote(f: {
  batches: number | null
  batches_by_weight?: number | null
  used_in?: string[]
  blend_oz?: number
  oz_to_make: number
}): string | null {
  if (f.batches == null || f.batches_by_weight == null || f.batches <= f.batches_by_weight) return null
  const uses = [...(f.used_in ?? [])]
  if (f.oz_to_make - (f.blend_oz ?? 0) > 1e-9) uses.push('on its own')
  return `Weight needs ${f.batches_by_weight}; 1 batch each for ${uses.join(', ')}`
}

function flavorNotes(f: PrintPlanFlavor): string[] {
  const notes: string[] = []
  if (f.tier) notes.push(f.tier_min != null ? `${f.tier}, minimum ${f.tier_min}` : f.tier)
  if ((f.made_from ?? []).length > 0) {
    const parts = (f.made_from ?? []).map(m => (m.pct != null ? `${m.label_name} ${m.pct}%` : m.label_name))
    notes.push(...wrap(`Blend of ${parts.join(', ')}`, 60))
  }
  if ((f.blend_oz ?? 0) > 0) {
    notes.push(...wrap(`Includes ${weight(f.blend_oz ?? 0)} for ${(f.used_in ?? []).join(', ')}`, 60))
  }
  const perUse = perUseBatchNote(f)
  if (perUse) notes.push(...wrap(perUse, 60))
  if (f.missing_weight) notes.push('Missing a bag weight')
  return notes
}

/** Builds the production plan as a printable PDF and opens it in a new tab. */
export function generateProductionPlanPdf(plan: PrintPlan): void {
  const flavors = plan.flavors
  // Recipe-only flavors (made only to mix into blends) print in their own section.
  const productFlavors = flavors.filter(f => !f.recipe_only)
  const recipeOnlyFlavors = flavors.filter(f => f.recipe_only)
  const sizes = (['Small', 'Large', 'Other'] as BagSize[]).filter(s =>
    productFlavors.some(f => f.products.some(p => p.size === s)),
  )
  const totalBags = flavors.reduce((sum, f) => sum + f.products.reduce((s, p) => s + p.to_make, 0), 0)
  const totalOz = flavors.reduce((sum, f) => sum + f.oz_to_make - (f.blend_oz ?? 0), 0)
  const generatedAt = new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  const doc = new Pages()

  // ── Header ─────────────────────────────────────────────────────────────────
  doc.push(text(MARGIN, doc.top - 18, 20, 'F2', 'Production Plan'))
  const basis =
    plan.includeForecast && plan.forecast && !plan.forecastStale
      ? `Open orders not printed yet plus next week's forecast (${formatDay(plan.forecast.weekStart)} - ${formatDay(plan.forecast.weekEnd)}), minus stock`
      : 'Open orders not printed yet, minus stock'
  doc.push(text(MARGIN, doc.top - 36, 10, 'F1', basis))
  doc.push(
    text(
      MARGIN,
      doc.top - 50,
      10,
      'F1',
      `${fmt(totalBags, 0)} bags · ${weight(totalOz)} of popcorn · ${productFlavors.length} flavors${
        recipeOnlyFlavors.length > 0 ? ` + ${recipeOnlyFlavors.length} recipe-only` : ''
      } · printed ${generatedAt}`,
    ),
  )
  doc.top -= 66

  // ── Flavors ────────────────────────────────────────────────────────────────
  const flavorHeader = (sizes: BagSize[]) => {
    doc.push(line(MARGIN, doc.top, RIGHT, doc.top, 1.2))
    const y = doc.top - 13
    doc.push(text(X_FLAVOR, y, 9.5, 'F2', 'Flavor'))
    sizes.forEach((s, i) => doc.push(text(SIZE_X[i] ?? 0, y, 9.5, 'F2', s === 'Other' ? 'Other' : s)))
    doc.push(text(X_WEIGHT, y, 9.5, 'F2', 'Weight'))
    doc.push(text(X_BATCHES, y, 9.5, 'F2', 'Batches'))
    doc.top -= 18
    doc.push(line(MARGIN, doc.top, RIGHT, doc.top, 1.2))
  }
  const drawFlavor = (f: PrintPlanFlavor, sizes: BagSize[]) => {
    const notes = flavorNotes(f)
    const height = 24 + notes.length * LINE_H
    if (doc.ensure(height)) flavorHeader(sizes)
    const y = doc.top - 15
    doc.push(strokeRect(X_BOX, y - 1, 9, 9, 0.8))
    doc.push(text(X_FLAVOR, y, 11, 'F2', truncate(f.label_name, 34)))
    notes.forEach((note, i) => doc.push(text(X_FLAVOR, y - 12 - i * LINE_H, 8.5, 'F1', note, 0.3)))

    sizes.forEach((s, i) => {
      const x = SIZE_X[i] ?? 0
      const p = f.products.find(prod => prod.size === s)
      if (!p) {
        doc.push(text(x, y, 11, 'F1', '-', 0.5))
        return
      }
      doc.push(text(x, y, 11, 'F2', String(p.to_make)))
      doc.push(text(x, y - 12, 7.5, 'F1', sizeDetail(p), 0.3))
    })

    doc.push(text(X_WEIGHT, y, 11, 'F1', weight(f.oz_to_make)))
    if (f.blend_by_percent) {
      doc.push(text(X_BATCHES, y, 9, 'F1', 'Mix only', 0.3))
    } else if (f.batches != null) {
      doc.push(text(X_BATCHES, y, 11, 'F2', String(f.batches)))
      doc.push(text(X_BATCHES, y - 12, 7.5, 'F1', `of ${weight(f.base_batch_weight_oz ?? 0)}`, 0.3))
    } else {
      doc.push(text(X_BATCHES, y, 9, 'F1', f.has_recipe ? 'No batch wt' : 'No recipe', 0.3))
    }

    doc.top -= height
    doc.push(line(MARGIN, doc.top, RIGHT, doc.top, 0.5, 0.75))
  }

  if (flavors.length === 0) {
    flavorHeader(sizes)
    doc.push(text(X_FLAVOR, doc.top - 16, 11, 'F1', 'Nothing to make right now.'))
    doc.top -= 24
  }

  if (productFlavors.length > 0) {
    flavorHeader(sizes)
    for (const f of productFlavors) drawFlavor(f, sizes)
  }

  if (sizes.length > 0) {
    if (doc.ensure(16)) flavorHeader(sizes)
    doc.push(
      text(X_FLAVOR, doc.top - 12, 7.5, 'F1', 'Under each size: ordered + forecast - in stock.', 0.3),
    )
    doc.top -= 16
  }

  // ── Recipe-only flavors ────────────────────────────────────────────────────
  if (recipeOnlyFlavors.length > 0) {
    doc.ensure(90)
    doc.top -= 20
    doc.push(text(MARGIN, doc.top - 14, 14, 'F2', 'Recipe-only Flavors'))
    doc.push(
      text(MARGIN, doc.top - 28, 9, 'F1', 'Not sold on their own; made only to mix into the blends above.', 0.3),
    )
    doc.top -= 38
    flavorHeader([])
    for (const f of recipeOnlyFlavors) drawFlavor(f, [])
  }

  // ── Ingredients ────────────────────────────────────────────────────────────
  const ingredientHeader = () => {
    doc.push(line(MARGIN, doc.top, RIGHT, doc.top, 1.2))
    const y = doc.top - 13
    doc.push(text(X_INGREDIENT, y, 9.5, 'F2', 'Ingredient'))
    doc.push(text(X_AMOUNT, y, 9.5, 'F2', 'Amount'))
    doc.push(text(X_USED_IN, y, 9.5, 'F2', 'Used in'))
    doc.top -= 18
    doc.push(line(MARGIN, doc.top, RIGHT, doc.top, 1.2))
  }

  doc.ensure(90)
  doc.top -= 20
  doc.push(text(MARGIN, doc.top - 14, 14, 'F2', 'Ingredients Needed'))
  doc.push(text(MARGIN, doc.top - 28, 9, 'F1', 'For the whole batches above, from each flavor\'s recipe.', 0.3))
  doc.top -= 38
  ingredientHeader()

  if (plan.ingredients.length === 0) {
    doc.push(text(X_INGREDIENT, doc.top - 16, 11, 'F1', 'No recipes for the flavors in this plan yet.'))
    doc.top -= 24
  }

  for (const ing of plan.ingredients) {
    const usedIn = wrap(ing.flavors.join(', '), 46)
    const height = 10 + Math.max(1, usedIn.length) * 12
    if (doc.ensure(height)) ingredientHeader()
    const y = doc.top - 14
    doc.push(text(X_INGREDIENT, y, 10, 'F1', truncate(ing.name, 32)))
    doc.push(text(X_AMOUNT, y, 10, 'F2', formatAmount(ing.amount, ing.unit)))
    usedIn.forEach((l, i) => doc.push(text(X_USED_IN, y - i * 12, 9, 'F1', l, 0.25)))
    doc.top -= height
    doc.push(line(MARGIN, doc.top, RIGHT, doc.top, 0.5, 0.75))
  }

  doc.flush()

  // ── Emit & open in a new tab ───────────────────────────────────────────────
  const bytes = buildPdf(doc.pages)
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
